import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { MapPin, Search, Star, Store } from 'lucide-react'
import PageHero from '../shared/PageHero'
import { contentApi, VendorItem } from '../../services/contentApi'
import { getImageUrl, IMAGE_CONFIG } from '../../config/settings'

const VendorsPage = () => {
  const [vendors, setVendors] = useState<VendorItem[]>([])
  const [search, setSearch] = useState('')
  const [location, setLocation] = useState('Aonla')
  const [loading, setLoading] = useState(true)

  const load = (q?: string, loc?: string) => {
    setLoading(true)
    contentApi.getVendors(1, q, loc)
      .then((res) => setVendors(res.data))
      .catch(() => setVendors([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load(undefined, 'Aonla') }, [])

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <PageHero
        eyebrow="Verified hosts"
        title="Meet our vendors & property partners"
        subtitle="Dynamic listings from active hosts around Manauna Dham, Aonla, and Bareilly — with live room counts and ratings."
      >
        <form
          className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 max-w-3xl"
          onSubmit={(e) => { e.preventDefault(); load(search.trim() || undefined, location.trim() || undefined) }}
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendor name..." className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <button type="submit" className="px-5 py-3 rounded-xl bg-primary-500 font-semibold hover:bg-primary-600 transition-colors">Filter</button>
        </form>
      </PageHero>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">{[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <div key={i} className="h-64 rounded-2xl bg-gray-200 animate-pulse" />)}</div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-white border border-gray-200">
            <Store className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">No vendors match your search. Try a broader location or <Link to="/vendor/signup" className="text-primary-600 font-medium">become a host</Link>.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
            {vendors.map((vendor) => (
              <Link
                key={vendor.id}
                to={`/vendors/${vendor.username}`}
                className="group rounded-2xl bg-white border border-gray-200/80 p-5 shadow-sm hover:shadow-lg hover:border-primary-200 transition-all"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={vendor.photo ? getImageUrl(vendor.photo) : IMAGE_CONFIG.THUMBNAIL_FALLBACK}
                    alt={vendor.name}
                    className="h-16 w-16 rounded-2xl object-cover ring-2 ring-gray-100 group-hover:ring-primary-200 transition-all"
                  />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-bold text-gray-900 truncate group-hover:text-primary-700">{vendor.name}</h2>
                    <p className="text-xs text-gray-500 truncate">@{vendor.username}</p>
                    {vendor.avg_rating > 0 && (
                      <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-gray-700">
                        <Star className="h-3.5 w-3.5 fill-primary-500 text-primary-500" /> {vendor.avg_rating.toFixed(1)}
                      </p>
                    )}
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-600 line-clamp-2">{vendor.address || `${vendor.city}${vendor.state ? `, ${vendor.state}` : ''}`}</p>
                <div className="mt-4 flex gap-3 text-xs font-medium text-gray-500">
                  <span>{vendor.room_count} rooms</span>
                  <span>·</span>
                  <span>{vendor.package_count} packages</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export default VendorsPage
